import React, { useState, useEffect } from 'react';
import {
  optimizationService,
  type DeclineAlertItem,
  type CompetitorItem,
  type OpportunityItem,
  type AlgorithmUpdateItem,
  type AlgorithmUpdateDetail,
} from '../../../services/optimizationService';
import { DiagnosticsModal } from './optimization/DiagnosticsModal';
import { CompetitorGapsModal } from './optimization/CompetitorGapsModal';
import { AlgorithmDetailModal } from './optimization/AlgorithmDetailModal';

interface EcosystemMonitorProps {
  onTooltipShow: (title: string, content: string) => void;
  initialTab?: string;
}

export default function EcosystemMonitor({ onTooltipShow }: EcosystemMonitorProps) {
  const [declines, setDeclines] = useState<DeclineAlertItem[]>([]);
  const [competitors, setCompetitors] = useState<CompetitorItem[]>([]);
  const [opportunities, setOpportunities] = useState<OpportunityItem[]>([]);
  const [updates, setUpdates] = useState<AlgorithmUpdateItem[]>([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modals state
  const [selectedDeclineDiag, setSelectedDeclineDiag] = useState<DeclineAlertItem | null>(null);
  const [selectedCompetitorGaps, setSelectedCompetitorGaps] = useState<CompetitorItem | null>(null);
  const [selectedUpdateDetail, setSelectedUpdateDetail] = useState<AlgorithmUpdateDetail | null>(null);
  const [loadingUpdateId, setLoadingUpdateId] = useState<string | null>(null);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [declinesRes, competitorsRes, opportunitiesRes, updatesRes] = await Promise.all([
        optimizationService.getDeclines('all'),
        optimizationService.getCompetitors(),
        optimizationService.getOpportunities('all'),
        optimizationService.getAlgorithmUpdates(),
      ]);

      if (declinesRes.status === 'success' && Array.isArray(declinesRes.data)) {
        setDeclines(declinesRes.data);
      } else {
        setDeclines([]);
      }

      if (competitorsRes.status === 'success' && Array.isArray(competitorsRes.data)) {
        setCompetitors(competitorsRes.data);
      } else {
        setCompetitors([]);
      }

      if (opportunitiesRes.status === 'success' && Array.isArray(opportunitiesRes.data)) {
        setOpportunities(opportunitiesRes.data);
      } else {
        setOpportunities([]);
      }

      if (updatesRes.status === 'success' && Array.isArray(updatesRes.data)) {
        setUpdates(updatesRes.data);
      } else {
        setUpdates([]);
      }
    } catch (err: any) {
      console.error('Failed to load ecosystem monitor data:', err);
      setError(err?.message || 'Failed to connect to optimization service.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenUpdateDetail = async (upd: AlgorithmUpdateItem) => {
    setLoadingUpdateId(upd.id);
    try {
      const res = await optimizationService.getAlgorithmUpdateDetail(upd.id);
      if (res.status === 'success' && res.data) {
        setSelectedUpdateDetail(res.data);
      } else {
        setSelectedUpdateDetail({
          ...upd,
          series: [],
          top_pages: upd.top_pages || [],
          adjustments: upd.adjustments || [],
        });
      }
    } catch (err) {
      console.error('Failed to load update detail:', err);
      setSelectedUpdateDetail({
        ...upd,
        series: [],
        top_pages: upd.top_pages || [],
        adjustments: upd.adjustments || [],
      });
    } finally {
      setLoadingUpdateId(null);
    }
  };

  // Helper to format search volume (e.g. 5400 -> "5.4K")
  const formatVolume = (vol: number | string | undefined | null) => {
    if (vol == null) return '0';
    if (typeof vol === 'string') return vol;
    if (vol >= 1000) {
      return `${(vol / 1000).toFixed(1)}K`;
    }
    return `${vol}`;
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Skeleton Loader when Loading Initial Data
  // ─────────────────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        {/* Header Skeleton */}
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <div className="h-7 w-56 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
            <div className="h-4 w-72 bg-gray-200 dark:bg-gray-700 rounded"></div>
          </div>
          <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
        </div>

        {/* Section 1 Skeleton */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-red-100 dark:border-red-900/30 p-6 space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-red-100 dark:bg-red-900/40 rounded-lg"></div>
              <div className="space-y-1.5">
                <div className="h-5 w-48 bg-gray-200 dark:bg-gray-700 rounded"></div>
                <div className="h-3 w-64 bg-gray-100 dark:bg-gray-800 rounded"></div>
              </div>
            </div>
            <div className="h-6 w-24 bg-red-100 dark:bg-red-900/30 rounded-full"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-36 bg-gray-100 dark:bg-gray-700/30 rounded-lg border border-red-100 dark:border-red-900/20 p-5 space-y-3">
                <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-3/4"></div>
                <div className="h-3 bg-gray-200 dark:bg-gray-600 rounded w-1/2"></div>
                <div className="h-2 bg-gray-200 dark:bg-gray-600 rounded w-full"></div>
              </div>
            ))}
          </div>
          <div className="h-16 bg-red-50 dark:bg-red-900/20 rounded-lg"></div>
        </div>

        {/* Section 2 Skeleton */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-orange-100 dark:border-orange-900/30 p-6 space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/40 rounded-lg"></div>
              <div className="space-y-1.5">
                <div className="h-5 w-48 bg-gray-200 dark:bg-gray-700 rounded"></div>
                <div className="h-3 w-64 bg-gray-100 dark:bg-gray-800 rounded"></div>
              </div>
            </div>
            <div className="h-6 w-24 bg-orange-100 dark:bg-orange-900/30 rounded-full"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-36 bg-gray-100 dark:bg-gray-700/30 rounded-lg border border-orange-100 dark:border-orange-900/20 p-5 space-y-3">
                <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-3/4"></div>
                <div className="h-3 bg-gray-200 dark:bg-gray-600 rounded w-full"></div>
                <div className="h-3 bg-gray-200 dark:bg-gray-600 rounded w-full"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header matching Prototype */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Ecosystem Monitor</h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Track competitive landscape and market changes
          </p>
        </div>
        <button
          onClick={() =>
            onTooltipShow(
              'Ecosystem Monitor',
              'Monitor your competitive landscape, algorithm updates, and emerging opportunities. Stay ahead of market changes and adapt your strategy proactively.'
            )
          }
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
        >
          <i className="ri-information-line text-xl"></i>
        </button>
      </div>

      {/* Error state if any */}
      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-xs text-red-800 dark:text-red-300 flex items-center justify-between">
          <span>{error}</span>
          <button
            onClick={fetchAllData}
            className="px-3 py-1 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      )}

      {/* ===================================================================== */}
      {/* 1. Performance Decline Alert (Red / Crimson Theme)                   */}
      {/* ===================================================================== */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-red-200 dark:border-red-800 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center">
              <i className="ri-arrow-down-line text-red-600 dark:text-red-400 text-xl"></i>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Performance Decline Alert</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">Keywords showing significant ranking drops</p>
            </div>
          </div>
          <span className="px-3 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-sm font-medium rounded-full">
            {declines.length} Keywords
          </span>
        </div>

        {declines.length === 0 ? (
          <div className="py-8 text-center text-xs text-gray-400 dark:text-gray-500 italic mb-6">
            No keyword ranking drops recorded for this period.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {declines.map((alert) => {
              const trafficImpact = Math.abs(alert.click_loss_pct || 0);
              return (
                <div
                  key={alert.id}
                  onClick={() => setSelectedDeclineDiag(alert)}
                  className="bg-red-50 dark:bg-red-900/20 p-5 rounded-lg border border-red-200 dark:border-red-800 cursor-pointer hover:border-red-400 dark:hover:border-red-700 transition-colors"
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-semibold text-gray-900 dark:text-white truncate flex-1 mr-2">
                      "{alert.keyword}"
                    </span>
                    <span className="text-xs bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300 px-2 py-1 rounded-full font-medium whitespace-nowrap">
                      Pos. {alert.position_now ?? '—'}
                    </span>
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                    Previously: Position {alert.position_prev ?? '—'}
                  </div>
                  <div className="flex items-center space-x-2 text-xs text-red-600 dark:text-red-400 font-medium mb-4">
                    <i className="ri-arrow-down-line"></i>
                    <span>Dropped {alert.position_delta} positions</span>
                  </div>

                  <div>
                    <div className="flex items-center justify-between text-xs mb-2">
                      <span className="text-gray-600 dark:text-gray-400">Traffic Impact</span>
                      <span className="font-semibold text-red-600 dark:text-red-400">-{trafficImpact}%</span>
                    </div>
                    <div className="h-2 bg-red-100 dark:bg-red-900/40 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-red-500 rounded-full transition-all duration-500"
                        style={{ width: `${Math.min(trafficImpact, 100)}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Disclaimer at bottom */}
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-start space-x-3">
            <i className="ri-alert-line text-red-600 dark:text-red-400 text-lg mt-0.5"></i>
            <div>
              <h4 className="font-medium text-red-900 dark:text-red-100 text-sm mb-1">Action Required</h4>
              <p className="text-sm text-red-800 dark:text-red-200">
                These keyword drops require immediate attention. Consider reviewing your content quality, technical SEO factors, and competitor activities to identify potential causes and recovery strategies.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ===================================================================== */}
      {/* 2. Emerging Competitors (Orange / Amber Theme)                       */}
      {/* ===================================================================== */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-orange-200 dark:border-orange-800 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center">
              <i className="ri-user-add-line text-orange-600 dark:text-orange-400 text-xl"></i>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Emerging Competitors</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">New players entering your market space</p>
            </div>
          </div>
          <span className="px-3 py-1 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 text-sm font-medium rounded-full">
            {competitors.length} Detected
          </span>
        </div>

        {competitors.length === 0 ? (
          <div className="py-8 text-center text-xs text-gray-400 dark:text-gray-500 italic mb-6">
            No emerging competitor threats detected for this period.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {competitors.map((competitor) => {
              const isHigh = competitor.threat_level?.toLowerCase() === 'high';
              const threatLabel = isHigh ? 'High Threat' : 'Medium Threat';
              const firstSeenStr = competitor.first_seen_at
                ? new Date(competitor.first_seen_at).toISOString().split('T')[0]
                : '2026-08-01';
              const overlapping = competitor.shared_keywords ?? 0;
              const avgPos = competitor.domain_authority
                ? competitor.domain_authority.toFixed(1)
                : '18.5';
              const growth = competitor.velocity
                ? `+${Math.round(competitor.velocity * 5)}%`
                : '+15%';

              return (
                <div
                  key={competitor.id}
                  onClick={() => setSelectedCompetitorGaps(competitor)}
                  className="p-5 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800 cursor-pointer hover:border-orange-400 dark:hover:border-orange-700 transition-colors"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1 min-w-0 mr-2">
                      <h4 className="font-semibold text-gray-900 dark:text-white text-sm truncate">
                        {competitor.domain}
                      </h4>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">First seen: {firstSeenStr}</p>
                    </div>
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full whitespace-nowrap ml-2 ${
                        isHigh
                          ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                          : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400'
                      }`}
                    >
                      {threatLabel}
                    </span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Overlapping Keywords</span>
                      <span className="font-semibold text-gray-900 dark:text-white">{overlapping}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Avg. Position</span>
                      <span className="font-semibold text-gray-900 dark:text-white">{avgPos}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Growth Rate</span>
                      <span className="font-semibold text-orange-600 dark:text-orange-400">{growth}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Disclaimer at bottom */}
        <div className="p-4 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
          <div className="flex items-start space-x-3">
            <i className="ri-information-line text-orange-600 dark:text-orange-400 text-lg mt-0.5"></i>
            <div>
              <h4 className="text-sm font-semibold text-orange-900 dark:text-orange-300 mb-1">
                Competitive Intelligence
              </h4>
              <p className="text-sm text-orange-800 dark:text-orange-400">
                Monitor these emerging competitors closely. Analyzing their strategies and keyword targeting can help you identify gaps in your own approach and maintain your competitive advantage.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ===================================================================== */}
      {/* 3. Growth Opportunities (Green / Emerald Theme)                      */}
      {/* ===================================================================== */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-green-200 dark:border-green-800 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
              <i className="ri-lightbulb-line text-xl text-green-600 dark:text-green-400"></i>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Growth Opportunities</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">High-potential keywords with low competition</p>
            </div>
          </div>
          <span className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full text-sm font-medium">
            {opportunities.length} Keywords
          </span>
        </div>

        {opportunities.length === 0 ? (
          <div className="py-8 text-center text-xs text-gray-400 dark:text-gray-500 italic mb-6">
            No keyword growth opportunities identified for this period.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {opportunities.map((opp) => {
              const oppScoreLabel = opp.score >= 80 ? 'High' : opp.score >= 60 ? 'Medium' : 'Low';
              const reasonText =
                opp.insight ||
                opp.recommendation ||
                'Low competition with high search intent. Your domain authority gives you an advantage.';
              const isRising = opp.trend === 'rising';

              return (
                <div
                  key={opp.id}
                  className="p-5 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800 flex flex-col justify-between"
                >
                  <div>
                    <div className="flex items-start justify-between mb-3">
                      <h4 className="font-semibold text-gray-900 dark:text-white flex-1 text-sm truncate mr-2">
                        {opp.keyword}
                      </h4>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ml-2 ${
                          isRising
                            ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400'
                            : 'bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-400'
                        }`}
                      >
                        {isRising ? '↗ Rising' : '→ Stable'}
                      </span>
                    </div>
                    <div className="mb-3">
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Volume</p>
                          <p className="text-sm font-semibold text-gray-900 dark:text-white">
                            {formatVolume(opp.volume)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Difficulty</p>
                          <p className="text-sm font-semibold text-green-600 dark:text-green-400">
                            {opp.difficulty}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Score</p>
                          <p className="text-sm font-semibold text-green-600 dark:text-green-400">
                            {oppScoreLabel}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">{reasonText}</p>
                </div>
              );
            })}
          </div>
        )}

        {/* Disclaimer at bottom */}
        <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <div className="flex items-start space-x-3">
            <i className="ri-lightbulb-line text-green-600 dark:text-green-400 text-lg mt-0.5"></i>
            <div>
              <h4 className="font-medium text-green-900 dark:text-green-100 text-sm mb-1">Strategic Recommendation</h4>
              <p className="text-sm text-green-800 dark:text-green-200">
                These keywords represent valuable opportunities for your SEO strategy. Consider incorporating them into your content planning and optimization efforts to capture untapped search traffic.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ===================================================================== */}
      {/* 4. Algorithm Updates (Blue Theme)                                    */}
      {/* ===================================================================== */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-blue-200 dark:border-blue-800 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
              <i className="ri-google-line text-blue-600 dark:text-blue-400 text-xl"></i>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Algorithm Updates</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">Recent search engine changes</p>
            </div>
          </div>
          <button
            onClick={() =>
              onTooltipShow(
                'Algorithm Updates',
                'Stay informed about recent Google algorithm updates and their potential impact on your rankings. Follow recommendations to maintain or improve your positions.'
              )
            }
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <i className="ri-information-line text-lg"></i>
          </button>
        </div>

        {updates.length === 0 ? (
          <div className="py-8 text-center text-xs text-gray-400 dark:text-gray-500 italic mb-6">
            No search algorithm updates impacting domain rankings.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {updates.map((update) => {
              const impactLevel = update.impact_level
                ? update.impact_level.charAt(0).toUpperCase() + update.impact_level.slice(1)
                : 'Medium';
              const isHigh = update.impact_level?.toLowerCase() === 'high';
              const dateStr = update.started_at
                ? new Date(update.started_at).toISOString().split('T')[0]
                : '2026-08-15';
              const recommendation =
                update.recommendations?.[0] ||
                update.about ||
                'Authentic agency case studies and author credentials protected site ranking.';
              const affectedCount = update.pages_affected ?? 5;

              return (
                <div
                  key={update.id}
                  className="p-5 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 flex flex-col justify-between"
                >
                  <div>
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="font-semibold text-gray-900 dark:text-white text-sm">{update.name}</h4>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{dateStr}</p>
                      </div>
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full whitespace-nowrap ${
                          isHigh
                            ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                            : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400'
                        }`}
                      >
                        {impactLevel} Impact
                      </span>
                    </div>
                    <p className="text-sm text-gray-700 dark:text-gray-300 mb-3 leading-relaxed">{recommendation}</p>
                  </div>

                  <div className="flex items-center justify-between pt-3 border-t border-blue-200 dark:border-blue-700">
                    <span className="text-xs text-gray-600 dark:text-gray-400">
                      <i className="ri-file-list-line mr-1"></i>
                      {affectedCount} pages affected
                    </span>
                    <button
                      onClick={() => handleOpenUpdateDetail(update)}
                      disabled={loadingUpdateId === update.id}
                      className="text-xs text-blue-600 dark:text-blue-400 font-medium hover:text-blue-700 dark:hover:text-blue-300 whitespace-nowrap transition-colors"
                    >
                      {loadingUpdateId === update.id ? 'Loading...' : 'View Details →'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Disclaimer at bottom */}
        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <div className="flex items-start space-x-3">
            <i className="ri-information-line text-blue-600 dark:text-blue-400 text-lg mt-0.5"></i>
            <div>
              <h4 className="font-medium text-blue-900 dark:text-blue-100 text-sm mb-1">Stay Informed</h4>
              <p className="text-sm text-blue-800 dark:text-blue-200">
                Algorithm updates can significantly impact your rankings. Monitor these changes closely and implement recommended adjustments to maintain or improve your search visibility.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ===================================================================== */}
      {/* Interactive Modals                                                    */}
      {/* ===================================================================== */}

      {/* 1. 3-Pillar Diagnostics Modal */}
      {selectedDeclineDiag && (
        <DiagnosticsModal
          alert={selectedDeclineDiag}
          onClose={() => setSelectedDeclineDiag(null)}
        />
      )}

      {/* 2. Competitor Keyword Gaps Modal */}
      {selectedCompetitorGaps && (
        <CompetitorGapsModal
          competitor={selectedCompetitorGaps}
          onClose={() => setSelectedCompetitorGaps(null)}
        />
      )}

      {/* 3. Algorithm Update Detail Modal */}
      {selectedUpdateDetail && (
        <AlgorithmDetailModal
          detail={selectedUpdateDetail}
          onClose={() => setSelectedUpdateDetail(null)}
        />
      )}
    </div>
  );
}
